const normalizePermissionKeys = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item).trim().toUpperCase())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim().toUpperCase())
      .filter(Boolean);
  }

  return [];
};

const parsePermissionOverrides = (rawValue) => {
  if (!rawValue) {
    return { add: [], remove: [] };
  }

  try {
    const parsed = JSON.parse(rawValue);
    if (Array.isArray(parsed) || typeof parsed === "string") {
      return {
        add: normalizePermissionKeys(parsed),
        remove: [],
      };
    }

    if (parsed && typeof parsed === "object") {
      return {
        add: normalizePermissionKeys(parsed.add),
        remove: normalizePermissionKeys(parsed.remove),
      };
    }
  } catch (error) {
    return {
      add: normalizePermissionKeys(rawValue),
      remove: [],
    };
  }

  return { add: [], remove: [] };
};

const extractEffectivePermissionKeys = (source) => {
  const raw =
    source?.effectivePermissions ??
    source?.permissionsEffective ??
    source?.permisosEfectivos ??
    source?.effectivePermissionKeys ??
    source;

  return normalizePermissionKeys(raw);
};

const toModuleKey = (permissionKey) => {
  if (!permissionKey) return "";
  return String(permissionKey).toUpperCase().split(".")[0].trim();
};

const toMappedModuleKey = (permissionKey, moduleAliasMap = {}) => {
  const moduleKey = toModuleKey(permissionKey);
  return moduleAliasMap[moduleKey] || moduleKey;
};

const resolveSidebarModuleKeys = ({
  userLevel,
  levelPermissionKeys,
  effectivePermissionKeys,
  extraPermissionKeys,
  blockedPermissionKeys,
  allowedModuleKeys = [],
  moduleAliasMap = {},
}) => {
  const basePermissions = normalizePermissionKeys(levelPermissionKeys[userLevel] || []).map(
    (permissionKey) => toMappedModuleKey(permissionKey, moduleAliasMap)
  );
  const extraModules = normalizePermissionKeys(extraPermissionKeys).map((permissionKey) =>
    toMappedModuleKey(permissionKey, moduleAliasMap)
  );
  const blocked = new Set(
    normalizePermissionKeys(blockedPermissionKeys).map((permissionKey) =>
      toMappedModuleKey(permissionKey, moduleAliasMap)
    )
  );

  const effectiveModules = normalizePermissionKeys(effectivePermissionKeys)
    .map((permissionKey) => toMappedModuleKey(permissionKey, moduleAliasMap))
    .filter(Boolean);

  if (effectiveModules.length > 0) {
    const uniqueEffectiveModules = [...new Set(effectiveModules)];
    if (allowedModuleKeys.length === 0) {
      return uniqueEffectiveModules;
    }

    const allowedSet = new Set(normalizePermissionKeys(allowedModuleKeys));
    const validEffectiveModules = uniqueEffectiveModules.filter((moduleKey) =>
      allowedSet.has(moduleKey)
    );

    // Si no hay modulos validos en effectivePermissions,
    // se conserva la logica base por nivel + overrides.
    if (validEffectiveModules.length > 0) {
      return [...basePermissions, ...extraModules, ...validEffectiveModules].filter(
        (permissionKey, index, list) =>
          list.indexOf(permissionKey) === index && !blocked.has(permissionKey)
      );
    }
  }

  const merged = [...basePermissions, ...extraModules].filter(
    (permissionKey, index, list) =>
      list.indexOf(permissionKey) === index && !blocked.has(permissionKey)
  );

  return merged;
};

const hasPermission = (permissionKeys, requiredPermission) => {
  const normalizedKeys = new Set(normalizePermissionKeys(permissionKeys));
  const target = String(requiredPermission || "").trim().toUpperCase();

  if (!target) return false;
  if (normalizedKeys.has(target)) return true;

  // Permite herencia por prefijo: RH cubre RH.VACACIONES.VER
  const chunks = target.split(".");
  for (let i = chunks.length - 1; i > 0; i -= 1) {
    const parent = chunks.slice(0, i).join(".");
    if (normalizedKeys.has(parent)) {
      return true;
    }
  }

  return false;
};

export {
  normalizePermissionKeys,
  parsePermissionOverrides,
  extractEffectivePermissionKeys,
  resolveSidebarModuleKeys,
  hasPermission,
};
